import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function HeroSection() {
  const hotArticles = articles.filter((a) => a.isHot).slice(0, 3);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % hotArticles.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [hotArticles.length]);

  const article = hotArticles[current];

  return (
    <section className="relative w-full h-[480px] md:h-[560px] overflow-hidden">
      <img
        src={article.imageUrl}
        alt={article.title}
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60"></div>
      <div className="absolute inset-0 flex items-end">
        <div className="max-w-[1280px] mx-auto w-full px-4 md:px-6 pb-12 md:pb-16">
          <div className="max-w-2xl">
            <Link to={`/article/${article.id}`} className="block group cursor-pointer">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary-500 text-background-50">
                  热门爆料
                </span>
                <span className="text-sm text-white/80">{article.publishDate}</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-white leading-snug group-hover:text-primary-200 transition-colors">
                {article.title}
              </h2>
              <p className="text-sm md:text-base text-white/70 mt-3 line-clamp-2 leading-relaxed">
                {article.excerpt}
              </p>
            </Link>
            <div className="flex items-center gap-3 mt-6">
              {hotArticles.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrent(idx)}
                  className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                    idx === current ? 'w-8 bg-accent-500' : 'w-1.5 bg-white/50 hover:bg-white/70'
                  }`}
                  aria-label={`切换到第${idx + 1}张`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="bg-background-50 border-b border-background-200">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
          <Link
            to="/"
            className="flex-shrink-0 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            全部
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className={`${cat.icon} text-primary-500`}></i>
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotTopics() {
  const hotArticles = articles.filter((a) => a.isHot).slice(0, 5);

  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
            🔥 热门爆料
          </h2>
          <Link to="/category/yulequan" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer flex items-center gap-1">
            查看更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {hotArticles.slice(0, 3).map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>

        <div className="mt-5 bg-background-100 rounded-lg p-4 md:p-5">
          <h3 className="font-heading text-base font-bold text-foreground-800 mb-3">爆料热榜</h3>
          <div className="flex flex-col gap-1">
            {hotArticles.map((article, idx) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="flex items-center gap-3 py-2.5 px-3 rounded-md hover:bg-background-200 transition-colors cursor-pointer group"
              >
                <span
                  className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx < 3
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-300 text-foreground-600'
                  }`}
                >
                  {idx + 1}
                </span>
                <span className="flex-1 text-sm text-foreground-700 line-clamp-1 group-hover:text-primary-600 transition-colors">
                  {article.title}
                </span>
                <span className="flex-shrink-0 text-xs text-foreground-400">
                  {(article.readCount / 10000).toFixed(1)}万阅读
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function LatestNews() {
  const latestArticles = articles.slice().sort(
    (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );

  return (
    <section className="py-10 md:py-14 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
            📰 最新爆料
          </h2>
        </div>

        <div className="flex flex-col gap-4">
          {latestArticles.slice(0, 10).map((article) => (
            <ArticleCard key={article.id} article={article} variant="horizontal" />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryGrid() {
  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
          爆料分类
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex flex-col items-center gap-2 p-5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 group-hover:bg-primary-100 transition-colors">
                <i className={`${cat.icon} text-2xl text-primary-500`}></i>
              </div>
              <span className="text-sm font-semibold text-foreground-700 group-hover:text-primary-600 transition-colors">
                {cat.name}
              </span>
              <span className="text-xs text-foreground-500 text-center leading-snug">
                {cat.description}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });
      const response = await fetch('https://readdy.ai/api/form/d9oqs4qqs38sj6jc7dvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '订阅失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <section className="py-10 md:py-14 bg-foreground-900">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-2">
          不想错过任何爆料？
        </h2>
        <p className="text-sm text-foreground-300 mb-6 max-w-md mx-auto">
          订阅吃瓜爆料推送，第一时间获取最新最热的吃瓜内容，永远走在吃瓜前线。
        </p>
        <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-readdy-form="" onSubmit={handleSubmit}>
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="honeypot-field"
          />
          <input
            type="email"
            name="email"
            placeholder="输入你的邮箱地址"
            className="flex-1 px-4 py-3 rounded-md bg-foreground-800 border border-foreground-700 text-background-50 placeholder:text-foreground-500 text-sm outline-none focus:border-accent-500 transition-colors"
            required
          />
          <button type="submit" className="px-6 py-3 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer transition-colors">
            立即订阅
          </button>
        </form>
        {formStatus === 'success' && (
          <p className="text-sm text-accent-400 mt-3">订阅成功！请查看邮箱确认。</p>
        )}
        {formStatus === 'error' && (
          <p className="text-sm text-red-400 mt-3">{formError}</p>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <ScrollToTop />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '吃瓜爆料 - 首页',
        description: '最新吃瓜爆料免费真实资讯，实时更新今日吃瓜热点与真实内幕爆料',
        url: 'https://udfujba.cn',
        isPartOf: {
          '@type': 'WebSite',
          name: '吃瓜爆料',
          url: 'https://udfujba.cn',
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://udfujba.cn' },
          ],
        },
      }) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <CategoryNav />
          <HotTopics />
          <LatestNews />
          <CategoryGrid />
          <NewsletterSection />
        </main>
        <Footer />
      </div>
    </>
  );
}